import { downloadBlob } from "@/shared/utils/downloadBlob";

export interface ShareCapableNavigator {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
}

export type ImageDelivery = "shared" | "downloaded" | "cancelled";

export interface ShareImageOptions {
  fileName: string;
  title: string;
  text?: string;
}

export interface ShareFileOptions extends ShareImageOptions {
  /** MIME type of the file, e.g. `application/pdf`. */
  type: string;
}

const browserNavigator = (): ShareCapableNavigator | undefined =>
  typeof navigator === "undefined" ? undefined : navigator;

/** Share the image itself where the platform can (Android, iOS, Windows); everywhere else, save it. */
export function pickShareStrategy(nav: ShareCapableNavigator | undefined, file: File): "share" | "download" {
  if (!nav?.share || !nav.canShare) return "download";
  try {
    return nav.canShare({ files: [file] }) ? "share" : "download";
  } catch {
    return "download";
  }
}

export function canShareFiles(
  type: string,
  nav: ShareCapableNavigator | undefined = browserNavigator(),
): boolean {
  return pickShareStrategy(nav, new File([], "probe", { type })) === "share";
}

export function canShareImages(nav: ShareCapableNavigator | undefined = browserNavigator()): boolean {
  return canShareFiles("image/png", nav);
}

export async function shareFile(
  content: Blob,
  { fileName, title, text, type }: ShareFileOptions,
  nav: ShareCapableNavigator | undefined = browserNavigator(),
): Promise<ImageDelivery> {
  const file = new File([content], fileName, { type });
  if (nav?.share && pickShareStrategy(nav, file) === "share") {
    try {
      await nav.share({ files: [file], title, text });
      return "shared";
    } catch (error) {
      // Closing the share sheet is a choice, not a failure; anything else falls back to saving the file.
      if ((error as { name?: unknown } | null)?.name === "AbortError") return "cancelled";
    }
  }
  downloadBlob(content, fileName);
  return "downloaded";
}

export function shareImage(
  image: Blob,
  options: ShareImageOptions,
  nav: ShareCapableNavigator | undefined = browserNavigator(),
): Promise<ImageDelivery> {
  return shareFile(image, { ...options, type: "image/png" }, nav);
}
